import { ITypedBaseResponse } from "@nkr/models/base-response";
import { IAssignCenterOpoRequest } from "@nkr/models/strong-box";
import { AuthActionCreators } from "@nkr/store/authorization";
import { useAppHookContext } from "@nkr/utils/rest-api";
import { useCallback, useEffect, useState } from "react";

interface IRetVal {
    list: IAssignCenterOpoRequest[],
    loading: boolean,
    saving: boolean,
    error: string,
    assignCenters: (opoId: number, centers: IAssignCenterOpoRequest[]) => Promise<ITypedBaseResponse<IAssignCenterOpoRequest[]>>
}

const useStrongBoxOpoCenter = (opoId?: number): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IAssignCenterOpoRequest[]>([]);

    useEffect(() => {
        setError('');

        const fetchData = async () => {
            setLoading(true);

            let config = {
                url: `${__API__}/strongbox/opo/${opoId}/assigned-centers`,
            };

            try {
                const response: ITypedBaseResponse<IAssignCenterOpoRequest[]> = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.results && response.data.success) {
                    setList(response.data.results);
                } else {
                    setError('Unable to load data');
                    setList([]);
                }
            } catch (err) {
                setList([]);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        if (opoId) fetchData();

    }, [opoId]);

    const assignCenters = useCallback((opoId: number, centers: IAssignCenterOpoRequest[]) => {
        const postData = async () => {
            setSaving(true);

            let config = {
                url: `${__API__}/strongbox/opo/${opoId}/assign-centers`,
                data: centers
            };

            try {
                const response: ITypedBaseResponse<IAssignCenterOpoRequest[]> = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.success) {
                    setList(response.data.results);
                    return response;
                } else {
                    setError('Unable to assign center to OPO');
                }
            } catch (err) {
                setList([]);
                setError(err);
            } finally {
                setSaving(false);
            }
        }

        return postData();
    }, []);

    return { loading, saving, error, list, assignCenters };
}

export default useStrongBoxOpoCenter;
